import React, { useEffect, useState } from 'react';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../../../components/InputControl/InputControl';
import { DEFAULT_LABEL_CLASS } from '../../../../components/InputControl/constants';
import { ButtonType } from '../../../../types/button-type';
import { ChangeControllerSettingsAction } from '../../../../enums/action';
import { changeControllerSettings, CONTROLLER_SETTINGS_LIMITS } from './settingsActions';
import { IControllerSettings } from '../../../../types/controller-types';

type PidPreset = { p: number; i: number; d: number };

type PidPresetKey = 'MILD' | 'MEDIUM' | 'POWERFUL';

const PID_PRESETS: Record<PidPresetKey, PidPreset> = {
  MILD: { p: 45, i: 0.4, d: 80 },
  MEDIUM: { p: 95, i: 0.4, d: 200 },
  POWERFUL: { p: 325, i: 0, d: 400 },
};

const PID_PRESET_BUTTONS: { key: PidPresetKey; text: string }[] = [
  { key: 'MILD', text: 'Mild' },
  { key: 'MEDIUM', text: 'Medium' },
  { key: 'POWERFUL', text: 'Powerful' },
];

type PidFormValues = {
  pid_p: number;
  pid_i: number;
  pid_d: number;
};

type Props = {
  controllerId: string;
  settings: IControllerSettings;
  allowedActionsForUser: ChangeControllerSettingsAction[];
};

const getValuesFromSettings = (settings: IControllerSettings): PidFormValues => ({
  pid_p: Number(settings.pid_p?.value),
  pid_i: Number(settings.pid_i?.value),
  pid_d: Number(settings.pid_d?.value),
});

const PidControls: React.FC<Props> = ({ controllerId, settings, allowedActionsForUser }) => {
  const [customValues, setCustomValues] = useState<PidFormValues>(() => getValuesFromSettings(settings));
  const [applyingPreset, setApplyingPreset] = useState<PidPresetKey | null>(null);
  const [isSendingCustom, setIsSendingCustom] = useState(false);

  useEffect(() => {
    setCustomValues(getValuesFromSettings(settings));
  }, [settings.pid_p?.value, settings.pid_i?.value, settings.pid_d?.value]);

  const isAllowed =
    allowedActionsForUser.includes(ChangeControllerSettingsAction.PID_GAINS_CHANGE_P) &&
    allowedActionsForUser.includes(ChangeControllerSettingsAction.PID_GAINS_CHANGE_I) &&
    allowedActionsForUser.includes(ChangeControllerSettingsAction.PID_GAINS_CHANGE_D);

  const sendPidGains = async (p: number, i: number, d: number) => {
    await Promise.all([
      changeControllerSettings(controllerId, ChangeControllerSettingsAction.PID_GAINS_CHANGE_P, String(p)),
      changeControllerSettings(controllerId, ChangeControllerSettingsAction.PID_GAINS_CHANGE_I, String(i)),
      changeControllerSettings(controllerId, ChangeControllerSettingsAction.PID_GAINS_CHANGE_D, String(d)),
    ]);
  };

  const handlePreset = async (presetKey: PidPresetKey) => {
    const preset = PID_PRESETS[presetKey];
    setApplyingPreset(presetKey);
    try {
      await sendPidGains(preset.p, preset.i, preset.d);
      setCustomValues({ pid_p: preset.p, pid_i: preset.i, pid_d: preset.d });
    } finally {
      setApplyingPreset(null);
    }
  };

  const handleCustomFieldChange = (field: keyof PidFormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? NaN : Number(event.target.value);
    setCustomValues((prev) => ({ ...prev, [field]: value }));
  };

  const isCustomValid = (value: number, limits: { MIN: number; MAX: number }) =>
    !isNaN(value) && value >= limits.MIN && value <= limits.MAX;

  const isCustomFormValid =
    isCustomValid(customValues.pid_p, CONTROLLER_SETTINGS_LIMITS.PID_P) &&
    isCustomValid(customValues.pid_i, CONTROLLER_SETTINGS_LIMITS.PID_I) &&
    isCustomValid(customValues.pid_d, CONTROLLER_SETTINGS_LIMITS.PID_D);

  const handleSendCustom = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isCustomFormValid) return;
    setIsSendingCustom(true);
    try {
      await sendPidGains(customValues.pid_p, customValues.pid_i, customValues.pid_d);
    } finally {
      setIsSendingCustom(false);
    }
  };

  if (!isAllowed) {
    return null;
  }

  return (
    <div className="flex w-full flex-col justify-start p-7">
      <h2 className="c-input-control__label flex flex-wrap items-baseline gap-3 pb-2.5 font-medium text-black dark:text-white">
        <span className="text-lg font-bold 2xl:text-xl">PID controls</span>
        <span className="text-sm font-normal text-slate-gray dark:text-gray-400">
          {`Current: P ${settings.pid_p?.value ?? '-'} · I ${settings.pid_i?.value ?? '-'} · D ${settings.pid_d?.value ?? '-'}`}
        </span>
      </h2>
      <div className="flex w-full flex-row flex-wrap justify-start gap-5.5 pb-4">
        {PID_PRESET_BUTTONS.map(({ key, text }) => (
          <InteractiveButton
            key={key}
            text={text}
            type={ButtonType.PrimaryInverse}
            onClick={() => handlePreset(key)}
            isLoading={applyingPreset === key}
            disabled={!!applyingPreset || isSendingCustom}
          />
        ))}
      </div>
      <form className="flex w-full flex-col items-start gap-4 md:flex-row md:items-end" onSubmit={handleSendCustom}>
        <InputControl
          label="P"
          type="number"
          name="pid_p"
          value={isNaN(customValues.pid_p) ? '' : customValues.pid_p}
          customOnChange={handleCustomFieldChange('pid_p')}
          min={CONTROLLER_SETTINGS_LIMITS.PID_P.MIN}
          max={CONTROLLER_SETTINGS_LIMITS.PID_P.MAX}
          step="any"
        />
        <InputControl
          label="I"
          type="number"
          name="pid_i"
          value={isNaN(customValues.pid_i) ? '' : customValues.pid_i}
          customOnChange={handleCustomFieldChange('pid_i')}
          min={CONTROLLER_SETTINGS_LIMITS.PID_I.MIN}
          max={CONTROLLER_SETTINGS_LIMITS.PID_I.MAX}
          step="any"
        />
        <InputControl
          label="D"
          type="number"
          name="pid_d"
          value={isNaN(customValues.pid_d) ? '' : customValues.pid_d}
          customOnChange={handleCustomFieldChange('pid_d')}
          min={CONTROLLER_SETTINGS_LIMITS.PID_D.MIN}
          max={CONTROLLER_SETTINGS_LIMITS.PID_D.MAX}
          step="any"
        />
        <div className="flex flex-col">
          <label className={`invisible ${DEFAULT_LABEL_CLASS}`}>Send</label>
          <InteractiveButton
            text="Send Custom"
            submit
            isLoading={isSendingCustom}
            disabled={!!applyingPreset || !isCustomFormValid}
            className="h-13 2xl:h-15"
          />
          <div className="min-h-6" />
        </div>
      </form>
    </div>
  );
};

export default PidControls;
